import { useEffect } from 'react';
import { useStore } from '@/legacy/store/zustand';
import { useUserInfo } from '@/features/space-user';
import { queryClient } from '@/legacy/lib/reactQuery';
import { fetchTableData } from '@/legacy/features/table/service';
import { TablePayloadToSend } from '@/legacy/features/table/types';
import { FIVE_MINUTES_IN_MS } from '@/legacy/constants';
import { resolveDateFilter } from '@/legacy/utils';

const PAGE = 1;
const PAGE_SIZE = 10;
const EMPTY_STABLE = '{}';

type PrefetchTableConfig = {
	pageKey: string;
	tableId: string;
	endpoint: string;
	sortBy: string;
	roles?: readonly string[];
	direction?: 'asc' | 'desc';
};

/**
 * Generic prefetch hook for table pages. Warms up the React Query cache
 * right after login so the target page renders instantly.
 *
 * - Guards by role: only runs when the current user's role is in `roles`.
 * - Calls `ensurePage` to initialise the date filter in the Zustand store
 *   before the page mounts, so both sides share the same filter reference
 *   and produce an identical query key.
 * - Query key and payload mirror `useTableData` defaults (page 1, 10 rows,
 *   no extra filters).
 *
 * Should be called via a named wrapper hook from `AppGate` after user info
 * has been loaded.
 */
export const usePrefetchTable = ({
	pageKey,
	tableId,
	endpoint,
	sortBy,
	roles,
	direction = 'asc',
}: PrefetchTableConfig) => {
	const { data: userInfo } = useUserInfo();
	const role = userInfo?.role;

	useEffect(() => {
		if (!role) return;

		if (roles && !roles.includes(role)) return;

		const { ensurePage } = useStore.getState();
		ensurePage(pageKey);

		const dateFilter = useStore.getState().filters[pageKey]?.date;
		const resolvedDate = resolveDateFilter(dateFilter);
		if (!resolvedDate?.startDate || !resolvedDate?.endDate) return;

		const queryKey = [
			tableId,
			endpoint,
			PAGE,
			PAGE_SIZE,
			sortBy,
			direction,
			EMPTY_STABLE,
			'',
			dateFilter,
			undefined,
			EMPTY_STABLE,
		];

		const body: TablePayloadToSend = {
			page: PAGE,
			pageSize: PAGE_SIZE,
			sort_by: sortBy,
			direction,
			filters: {},
			start_date: resolvedDate.startDate,
			end_date: resolvedDate.endDate,
			dates_logic: resolvedDate.dateLogic,
			search: '',
			use_cache: true,
			query_filters: {},
		};

		queryClient.prefetchQuery({
			queryKey,
			queryFn: () => fetchTableData(endpoint, body),
			staleTime: FIVE_MINUTES_IN_MS,
			gcTime: FIVE_MINUTES_IN_MS,
		});
	}, [role]);
};
